import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../auth/AuthContext";
import Countdown from "../components/Countdown";
import { Btn, Card, Spinner, G_DARK, globalStyles } from "../components/Shared";
import { PersonLabel } from "../components/Portal";

// The page a signatory signs a quotation on, at
// `/projects/:pid/quotations/:qid/sign`, behind the login.
//
// Until 2026-09-24 it was public and the mailed link carried a bearer token,
// so an out-of-office reply that handed the link to a facility mailbox handed
// over the signature with it. Now the server takes the signer from the
// session and refuses (403) an account that is not a signatory of this
// quotation. What the token used to pin — the exact document — is pinned by
// the hash: the page sends back the `document_hash` it was shown, and a
// quotation changed since is refused (409) and reloaded here.

// `kind` is the document's own word — "quotation", or "Statement of Work" for a
// `sow1` document (2026-10-28). Before the document has loaded the page cannot
// know which it is, and says "quotation", as it always has.
const notASignatory = (kind = "quotation") =>
  `This account is not a signatory of this ${kind}. Sign in as the person it was `
  + "sent to, or ask the facility who is meant to sign it.";
const documentChanged = (kind = "quotation") =>
  `The ${kind} was changed after you opened it, so nothing was recorded. The current `
  + "version is shown below — read it again before you decide.";
const OLD_LINK =
  "This link no longer signs anything. Sign in to the portal; the quotation is waiting "
  + "for you there.";

// A 403 is this page's own sentence; anything else the server worded.
const explain = (e, kind) => (e.status === 403 ? notASignatory(kind) : e.message);

// A Statement of Work (`document.v === "sow1"`) is signed without prices: the
// document carries no currency, totals, unit price or line total, and the
// group's note stands where a quotation prints its terms.
const isStatementOfWork = quote => quote?.document?.v === "sow1";

function shell(children) {
  return (
    <div style={{ minHeight: "100vh", background: "#f5f5f5", padding: "40px 20px" }}>
      {/* Outside Layout, so the variables G_DARK and Btn resolve against are
          defined here or nowhere: without this the enabled Accept button is
          white text on a transparent background. As LoginPage does. */}
      <style>{globalStyles}</style>
      <div style={{ maxWidth: 780, margin: "0 auto" }}>{children}</div>
    </div>
  );
}

// `/quotation?token=…`: a link mailed before 2026-09-24. Public, and it makes
// no request at all — the token it carries is never sent anywhere. A sentence
// and a way in, never a blank page.
export function OldSigningLinkPage() {
  const navigate = useNavigate();
  return shell(
    <Card>
      <h1 style={{ fontSize: 18, margin: "0 0 8px" }}>Quotation</h1>
      <p role="status" style={{ fontSize: 14, margin: "0 0 16px", color: "#374151" }}>{OLD_LINK}</p>
      <Btn onClick={() => navigate("/login")}>Sign in</Btn>
    </Card>
  );
}

function Money({ value, currency }) {
  return <>{value} {currency}</>;
}

export default function QuotationSignPage() {
  const { pid, qid }        = useParams();
  const base                = `/api/projects/${pid}/quotations/${qid}`;
  const refreshDuties       = useAuth()?.refreshDuties;
  const [quote, setQuote]   = useState(null);
  const [error, setError]   = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy]     = useState(false);
  const [done, setDone]     = useState(null);
  const [confirmed, setConfirmed] = useState(false);

  const load = useCallback(
    () => api.get(`${base}/signing`).then(setQuote).catch(e => setError(explain(e))),
    [base]);

  useEffect(() => { load(); }, [load]);

  const sow  = isStatementOfWork(quote);
  // Lower case in a sentence; a proper name in a heading either way.
  const word = sow ? "Statement of Work" : "quotation";
  const Word = sow ? "Statement of Work" : "Quotation";

  const decide = async decision => {
    setBusy(true); setError(null); setNotice(null);
    try {
      // The hash of the document on screen, and nothing else: the signer is the
      // session's account, and the server refuses a hash that is not current.
      setDone(await api.post(`${base}/decide`,
                             { decision, document_hash: quote.document_hash }));
      // This page is outside Layout, so the banner would otherwise wait for the
      // next navigation to stop asking for the signature just given.
      refreshDuties?.();
    } catch (e) {
      if (e.status === 409) {
        // The text changed under the reader. Agreeing to the old one is what
        // the hash exists to prevent, so the tick is taken back as well.
        setConfirmed(false);
        setNotice(documentChanged(word));
        await load();
      } else {
        setError(explain(e, word));
      }
    } finally { setBusy(false); }
  };

  if (error && !quote) return shell(
    <Card>
      <h1 style={{ fontSize: 18, margin: "0 0 8px" }}>This quotation cannot be opened here</h1>
      <p role="alert" style={{ color: "#b91c1c", fontSize: 14, margin: 0 }}>{error}</p>
    </Card>
  );

  if (!quote) return shell(<Card><Spinner /> Loading…</Card>);

  const d = quote.document || {};
  // The caller's own signatory row. `signer` is the name the old view used;
  // `signatory` is accepted too, since the row is what the route returns.
  const signer = quote.signer || quote.signatory || {};
  const decided = done || quote.decided;
  // From the response to the decision when there is one, otherwise from the
  // page load. `done` carries the state as of the click, which is the moment
  // the person is actually reading this.
  const awaiting = (done && done.awaiting) || quote.awaiting || [];
  const decision = done?.status || quote.decision;
  // The quotation's own state, from the decision when there has just been one and
  // from the page load otherwise — the same precedence as `awaiting` above, and
  // it is what catches the signatory who declines while this page is open.
  //
  // A quotation that is over is over, and `awaiting` does not say so: it answers
  // "required, and has not accepted", which neither a withdrawal nor a refusal
  // changes. The `/signing` route refuses a withdrawn quotation with a 409, so
  // the page is normally never rendered for one; this is what a page loaded
  // before the withdrawal shows. A rejected quotation is NOT refused there, on
  // purpose — the person who declined may reopen this page and is owed their
  // own receipt — so this is the only thing standing between a co-signatory and
  // two buttons the API will refuse at the end of them.
  const status     = done?.status || quote.status;
  const withdrawn  = status === "withdrawn";
  // One refusal rejects the whole offer (`status_after_decision`), so nobody
  // else is being waited on and there is nothing left to agree to.
  const rejected   = status === "rejected";

  return shell(
    <>
      <Card style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 20, margin: "0 0 4px" }}>{Word} {quote.number}</h1>
        <div style={{ color: "#6b7280", fontSize: 13 }}>
          {d.seller?.name} · valid until {d.valid_until}
          {quote.expires_at && !decided && !withdrawn && !rejected && (
            <> · <Countdown until={quote.expires_at} expiredLabel={`this ${word} has expired`} /></>
          )}
        </div>

        <table className="responsive-table" style={{ width: "100%", borderCollapse: "collapse", marginTop: 20, fontSize: 14 }}>
          <thead>
            <tr style={{ borderBottom: "2px solid #e5e7eb", textAlign: "left" }}>
              <th style={{ padding: "8px 6px" }}>Service</th>
              <th style={{ padding: "8px 6px", textAlign: "right" }}>Qty</th>
              {!sow && <th style={{ padding: "8px 6px", textAlign: "right" }}>Unit price</th>}
              {!sow && <th style={{ padding: "8px 6px", textAlign: "right" }}>Total</th>}
            </tr>
          </thead>
          <tbody>
            {(d.items || []).map(i => (
              <tr key={i.position} style={{ borderBottom: "1px solid #f3f4f6" }}>
                <td data-label="Service" style={{ padding: "8px 6px" }}>{i.service}</td>
                <td data-label="Qty" style={{ padding: "8px 6px", textAlign: "right" }}>{i.qty} {i.unit}</td>
                {!sow && <td data-label="Unit price" style={{ padding: "8px 6px", textAlign: "right" }}>{i.unit_price}</td>}
                {!sow && <td data-label="Total" style={{ padding: "8px 6px", textAlign: "right" }}>{i.line_total}</td>}
              </tr>
            ))}
          </tbody>
        </table>

        {/* No amount of any kind on a Statement of Work: the customer signs
            the scope, and the prices stay with staff and the close-out record. */}
        {!sow && <div style={{ marginTop: 14, textAlign: "right", fontSize: 15 }}>
          <div style={{ color: "#6b7280", fontSize: 13 }}>
            Subtotal <Money value={d.totals?.subtotal} currency={d.currency} />
          </div>
          <div style={{ color: "#6b7280", fontSize: 13 }}>
            VAT ({d.totals?.vat_rate}) <Money value={d.totals?.vat_amount} currency={d.currency} />
          </div>
          <div style={{ fontWeight: 700, marginTop: 4 }}>
            Total <Money value={d.totals?.total} currency={d.currency} />
          </div>
        </div>}

        {sow && d.note && (
          <div style={{ marginTop: 20, whiteSpace: "pre-wrap", fontSize: 13, color: "#374151" }}>
            <strong>Note</strong><br />{d.note}
          </div>
        )}

        {!sow && d.terms && (
          <div style={{ marginTop: 20, whiteSpace: "pre-wrap", fontSize: 13, color: "#374151" }}>
            <strong>Terms</strong><br />{d.terms}
          </div>
        )}
      </Card>

      <Card>
        {withdrawn ? (
          <div role="status">
            <h2 style={{ fontSize: 16, margin: "0 0 6px", color: "#b45309" }}>
              This {word} was withdrawn
            </h2>
            {/* The signatory did nothing wrong, and a bare refusal would send
                them looking for a fault of their own. */}
            <p style={{ margin: 0, color: "#6b7280", fontSize: 13 }}>
              It was taken off the table after it was sent to you, so it can no longer be
              signed. Nothing you did caused this — ask the facility whether a new {word}
              {" "}is coming.
            </p>
          </div>
        ) : decided ? (
          <div role="status">
            <h2 style={{ fontSize: 16, margin: "0 0 6px", color: decision === "accepted" ? G_DARK : "#b45309" }}>
              {decision === "accepted" ? "Accepted" : "Declined"}
            </h2>
            <p style={{ margin: 0, color: "#6b7280", fontSize: 13 }}>
              Recorded with the date and time, your IP address and the hash of the document
              shown above.
            </p>
            {/* "Nothing further is needed" is only true when it is. With
                several signatories, telling the first person the job is done
                is how a quotation sits untouched for a fortnight while everybody
                waits for everybody else.

                The refusal has to be taken out of that first, though: `decide`
                answers with `awaiting` exactly as the helper builds it, so the
                person who has just declined is on the list and was being told
                the quotation was still waiting for THEM. */}
            {rejected ? (
              <p style={{ margin: "8px 0 0", color: "#6b7280", fontSize: 13 }}>
                One refusal ends the {word}, so nobody else is being asked to sign it.
              </p>
            ) : awaiting.length > 0 ? (
              <p style={{ margin: "8px 0 0", color: "#b45309", fontSize: 13 }}>
                This {word} is not complete yet — it is still waiting for{" "}
                {awaiting.map((s, i) => (
                  <span key={s.user_id ?? s.email ?? i}>{i > 0 && ", "}<PersonLabel name={s.name} email={s.email} /></span>
                ))}.
              </p>
            ) : (
              <p style={{ margin: "8px 0 0", color: "#6b7280", fontSize: 13 }}>
                Nothing further is needed.
              </p>
            )}
          </div>
        ) : rejected ? (
          <div role="status">
            <h2 style={{ fontSize: 16, margin: "0 0 6px", color: "#b45309" }}>
              This {word} was declined
            </h2>
            {/* Not "you declined it" — this is the person who did not. Somebody
                who had to agree said no, which ends it for everybody, and that
                is the sentence rather than a 409 on the last click. */}
            <p style={{ margin: 0, color: "#6b7280", fontSize: 13 }}>
              Somebody who had to agree to it has declined, so there is nothing left to
              sign. Nothing you did caused this — ask the facility whether a new {word}
              {" "}is coming.
            </p>
          </div>
        ) : (
          <>
            <h2 style={{ fontSize: 16, margin: "0 0 10px" }}>Your response</h2>
            {error && (
              <div role="alert" style={{ marginBottom: 12, padding: "10px 12px", borderRadius: 8, background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.25)", color: "#b91c1c", fontSize: 13 }}>
                {error}
              </div>
            )}
            {notice && (
              <div role="status" style={{ marginBottom: 12, padding: "10px 12px", borderRadius: 8, background: "rgba(180,83,9,0.06)", border: "1px solid rgba(180,83,9,0.25)", color: "#92400e", fontSize: 13 }}>
                {notice}
              </div>
            )}
            {/* Shown, not asked for. `decide` records the signatory row of the
                signed-in account and nothing from the request but the decision
                and the hash, so a field here would only let somebody type a
                name the evidence then silently replaces. */}
            <div style={{ fontSize: 13, marginBottom: 6 }}>
              <div style={{ color: "#6b7280" }}>Signing as</div>
              <div style={{ fontSize: 14, fontWeight: 600, marginTop: 2 }}>
                {signer.name || signer.email}
              </div>
            </div>

            {/* The exact words being agreed to, shown before the button and
                stored with the signature. A record that says somebody accepted
                without saying what they accepted is not evidence of anything. */}
            <label style={{ display: "flex", gap: 8, alignItems: "flex-start", margin: "14px 0",
                            fontSize: 13, color: "#374151" }}>
              <input type="checkbox" checked={confirmed} style={{ marginTop: 3 }}
                     onChange={e => setConfirmed(e.target.checked)} />
              <span>{quote.intent_text}</span>
            </label>

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <Btn onClick={() => decide("accepted")} disabled={busy || !confirmed}>
                {busy ? <><Spinner /> Recording…</> : `Accept this ${word}`}
              </Btn>
              <Btn variant="secondary" onClick={() => decide("rejected")} disabled={busy}>
                Decline
              </Btn>
            </div>

            <p style={{ margin: "16px 0 0", color: "#6b7280", fontSize: 12 }}>
              Document {quote.algorithm}: <span data-break style={{ fontFamily: "'JetBrains Mono', monospace" }}>{quote.document_hash}</span>
              <br />
              Your decision is recorded against that exact document. If the {word} is
              {" "}changed before you decide, nothing is recorded and the new version is shown.
            </p>
          </>
        )}
      </Card>
    </>
  );
}
